// Cross-entity financial-document presentation policy: which totals row carries the grand-total
// emphasis, and what each document type is called. Shared because the binds differ per entity.
import { ActivityEntityName, PrepaymentSetting, prepaymentInvoiceTaxRate, QuotePolicy, roundQuotePrepayment } from '@jms/model';

// Grand-total binds across the five financial documents: Invoice/Bill/PO use `total`,
// Sale Order `totalInc`, Quote `totalSellInc`. Drives which summary row gets the emphasis.
export const GRAND_TOTAL_BINDS: ReadonlySet<string> = new Set(['total', 'grandTotal', 'totalInc', 'totalSellInc']);

export const FINANCIAL_DOCUMENT_LABELS: Partial<Record<ActivityEntityName, string>> = {
  [ActivityEntityName.Invoice]: 'Invoice',
  [ActivityEntityName.Bill]: 'Bill',
  [ActivityEntityName.QuotePro]: 'Quote',
  [ActivityEntityName.SaleOrder]: 'Sales Order',
  [ActivityEntityName.PurchaseOrder]: 'Purchase Order',
};

export function financialDocumentLabel(entity: string | undefined): string {

  return entity ? FINANCIAL_DOCUMENT_LABELS[entity as ActivityEntityName] ?? entity : 'Document';
}

/** The document heading. A Quote Template's identifier is its name, not a number: "Quote Template # Boarding"
 *  read as a numbering bug, so a template is headed by its name alone. `—` is the host's "no identifier yet". */
export function financialDocumentHeading(entity: string | undefined, identifier: string): string {

  const label = financialDocumentLabel(entity);
  if (identifier === '—') return `New ${label}`;
  return entity === ActivityEntityName.QuoteTemplate ? identifier : `${label} # ${identifier}`;
}

/** Emphasis is keyed on the grand-total BIND, not on list position: a schema that appends any
 *  further currency field after the total would otherwise silently move the emphasis to it.
 *  Falls back to the last row so an entity using an unrecognised total bind still marks one. */
export function isGrandTotalBind(bind: string, binds: readonly string[]): boolean {

  const explicit = binds.filter(candidate => GRAND_TOTAL_BINDS.has(candidate));
  const target = explicit.length ? explicit[explicit.length - 1] : binds[binds.length - 1];

  return target === bind;
}

const SELL_EX_BINDS: ReadonlySet<string> = new Set(['totalSellEx', 'totalEx', 'subTotal']);
const TAX_AMOUNT_BINDS: ReadonlySet<string> = new Set(['totalTax', 'tax']);

export interface FinancialDocumentPairSummaryRow {
  readonly label: string;
  readonly buyBind: string | null;
  readonly sellBind: string | null;
  readonly emphasis: boolean;
}

/** Two-column Buy|Sell footer when the schema authors `totalBuy` next to a sell total — Quote, not Invoice. */
export function financialDocumentPairSummary(binds: readonly string[]): readonly FinancialDocumentPairSummaryRow[] | null {

  if (!binds.includes('totalBuy')) return null;
  const sellEx = binds.find(bind => SELL_EX_BINDS.has(bind));
  const grand = binds.filter(bind => GRAND_TOTAL_BINDS.has(bind)).at(-1);
  if (!sellEx || !grand) return null;
  const tax = binds.find(bind => TAX_AMOUNT_BINDS.has(bind));
  return [
    { label: 'Subtotal', buyBind: 'totalBuy', sellBind: sellEx, emphasis: false },
    ...(tax ? [{ label: 'Tax', buyBind: null, sellBind: tax, emphasis: false }] : []),
    { label: 'Total', buyBind: 'totalBuy', sellBind: grand, emphasis: true },
  ];
}

export interface FinancialDocumentPrepaymentSummary {
  readonly inc: number;
  readonly tax: number;
}

/**
 * Prepayment Required row, shown only while the document's own policy has prepayments switched on —
 * the stored `prepaymentInc` survives a policy being turned off, so the figure alone is not consent
 * to bill it, and legacy gated the row on `policy.prepayments.enabled` for exactly that reason.
 *
 * The tax is what the prepayment invoice will charge: its rate from `prepaymentInvoiceTaxRate` (the
 * tenant setting arrives on `prepaymentSetting`, seeded by the quote surface) and its rounding. Stored
 * `prepaymentTax` is not the label: quotes saved before that rule carry a different figure.
 */
export function financialDocumentPrepaymentSummary(model: Record<string, unknown>): FinancialDocumentPrepaymentSummary | null {

  const policy = model['policy'] as QuotePolicy | undefined;
  if (!policy?.prepayments?.enabled) return null;
  const stored = Number(model['prepaymentInc']) || 0;
  if (stored <= 0) return null;
  const { prepaymentInc, prepaymentTax } = roundQuotePrepayment(stored, prepaymentInvoiceTaxRate(policy, model['prepaymentSetting'] as PrepaymentSetting | undefined));

  return { inc: prepaymentInc, tax: prepaymentTax };
}
